import { useState, useContext, type ChangeEvent } from "react";
import { useDisclosure } from "hooks/use-disclosure";

import { ConfirmationDialog } from "ui/confirmation-dialog";
import { LoadingButton } from "ui/button";
import { TextArea } from "ui/text-area";

import CommentContext from "contexts/comment-context";

import { TouchpointStatuses } from "types";

export interface TouchpointApproveButtonProps {
  onApprove: (status: string) => Promise<void>;
  isLoading: boolean;
}

export const TouchpointApproveButton = ({ onApprove, isLoading }: TouchpointApproveButtonProps) => {
  const confirmModal = useDisclosure();

  const openConfirmationModal = () => confirmModal.onOpen();
  const [comment, setComment] = useState<string>("");
  const { submitNewComment } = useContext(CommentContext);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  const handleApprove = async () => {
    try {
      if (comment) {
        const payload = {
          actionItem: true,
          text: comment,
        };
        await submitNewComment(payload);
      }

      await onApprove(TouchpointStatuses.APPROVED);

      confirmModal.onClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <LoadingButton
        state={isLoading ? "loading" : "default"}
        loadingMessage="Approving..."
        successMessage="Approved"
        errorMessage="Error. Try again."
        size="large"
        onClick={openConfirmationModal}>
        Approve
      </LoadingButton>

      <ConfirmationDialog
        open={confirmModal.isOpen}
        headline="Ready for final approval?"
        message="If you have any additional comments or feedback before this creative goes live, please add it here. Once we receive this approval, our team will move this through our QA and deployment process."
        cancelButtonText="Cancel"
        confirmButtonText="Approve"
        confirmLoadingMessage="Approving..."
        onConfirm={handleApprove}
        onClose={() => {
          confirmModal.onClose();
        }}
        intent="warning">
        <TextArea
          readOnly={false}
          label=""
          helperText=""
          value={comment}
          placeholder="Type..."
          onChange={(e) => {
            handleChange(e);
          }}
          data-testid="send-feedback-teaxtarea"
          // need to add ref to the input to focus on it
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleApprove();
            }
          }}
          resize="vertical"
        />
      </ConfirmationDialog>
    </>
  );
};
